import { Loading } from "../../app/Status";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import { formatEuro, type CompanyInsightsDto, type InsightBucketDto, type InsightPeriod } from "@klantpunt/shared";
import { IconRepeat } from "../../app/icons";
import { Meta } from "../../app/Meta";
import { buildingPath } from "../../app/navItems";
import { api } from "../../lib/api";
import { shortEuro } from "../mjop/BudgetChart";
import { companyPath } from "./parts";

/** Reactietijd leesbaar: "45 min", "3,5 uur", "2,1 dagen". */
export function fmtMinutes(m: number | null) {
  if (m == null) return "–";
  if (m < 60) return `${m} min`;
  if (m < 48 * 60) return `${(m / 60).toLocaleString("nl-NL", { maximumFractionDigits: 1 })} uur`;
  return `${(m / 1440).toLocaleString("nl-NL", { maximumFractionDigits: 1 })} dagen`;
}
/** Hele euro's: kengetallen hoeven geen centen. */
const euro0 = (cents: number) => `€ ${Math.round(cents / 100).toLocaleString("nl-NL")}`;
const fmtPct = (p: number | null) => (p == null ? "–" : `${p}%`);
const plural = (n: number, one: string, more: string) => `${n} ${n === 1 ? one : more}`;

/** Nette asverdeling: 0 en vier stappen tot boven het maximum. */
function ticks(max: number): number[] {
  if (max <= 0) return [0, 1];
  const raw = max / 4;
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= raw)!;
  return Array.from({ length: Math.ceil(max / step) + 1 }, (_, k) => k * step);
}

interface Series {
  key: string;
  label: string;
  className: string;
  value: (b: InsightBucketDto) => number;
}

/**
 * Staven per maand (of jaar), eventueel gestapeld, met een stippellijn voor een afspraak. Zelfde opmaak als de
 * MJOP-begroting (`.bchart`); hover en focus tonen de waarden van een staaf.
 */
function Bars({
  buckets,
  series,
  fmt,
  axis,
  goal,
  label,
}: {
  buckets: InsightBucketDto[];
  series: Series[];
  fmt: (v: number) => string;
  axis: (v: number) => string;
  goal?: { value: number; label: string } | null;
  label: string;
}) {
  const [hover, setHover] = useState<string | null>(null);
  const wrap = useRef<HTMLDivElement>(null);
  const [W, setW] = useState(640);
  useEffect(() => {
    const el = wrap.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setW(Math.max(260, Math.round(el.clientWidth))));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const H = Math.round(Math.min(300, Math.max(200, W * 0.32)));
  const pad = { l: 56, r: 8, t: 10, b: 28 };
  const totals = buckets.map((b) => series.reduce((n, s) => n + s.value(b), 0));
  const tk = ticks(Math.max(...totals, goal?.value ?? 0));
  const top = tk.at(-1)!;
  const iw = W - pad.l - pad.r;
  const ih = H - pad.t - pad.b;
  const slot = iw / Math.max(1, buckets.length);
  const bw = Math.min(32, slot * 0.6);
  const every = Math.max(1, Math.ceil(36 / slot));
  const yOf = (v: number) => pad.t + ih - (v / top) * ih;
  const hi = buckets.findIndex((b) => b.key === hover);
  const h = hi >= 0 ? buckets[hi]! : null;

  return (
    <figure className="bchart">
      {(series.length > 1 || goal) && (
        <ul className="bchart-legend" aria-hidden="true">
          {series.length > 1 &&
            series.map((s) => (
              <li key={s.key}>
                <i className={`wt ${s.className}`} />
                {s.label}
              </li>
            ))}
          {goal && (
            <li>
              <i className="ins-goal-key" />
              {goal.label}
            </li>
          )}
        </ul>
      )}
      <div className="bchart-wrap" ref={wrap}>
        <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={label}>
          {tk.map((v) => (
            <g key={v}>
              <line x1={pad.l} x2={W - pad.r} y1={yOf(v)} y2={yOf(v)} className="bchart-grid" />
              <text x={pad.l - 8} y={yOf(v)} className="bchart-axis" textAnchor="end" dominantBaseline="middle">
                {axis(v)}
              </text>
            </g>
          ))}
          {buckets.map((b, k) => {
            const x = pad.l + k * slot + (slot - bw) / 2;
            let acc = 0;
            const segs = series
              .filter((s) => s.value(b) > 0)
              .map((s) => {
                const y0 = yOf(acc);
                acc += s.value(b);
                const y1 = yOf(acc);
                return { s, y: y1, h: Math.max(0, y0 - y1) };
              });
            const text = `${b.label}: ${series.map((s) => `${s.label} ${fmt(s.value(b))}`).join(", ")}`;
            return (
              <g
                key={b.key}
                tabIndex={0}
                role="img"
                aria-label={text}
                className={`bchart-bar${hover === b.key ? " on" : ""}`}
                onMouseEnter={() => setHover(b.key)}
                onMouseLeave={() => setHover(null)}
                onFocus={() => setHover(b.key)}
                onBlur={() => setHover(null)}
              >
                <rect x={pad.l + k * slot} y={pad.t} width={slot} height={ih} className="bchart-hit" />
                {segs.map((g, i) => (
                  <rect
                    key={g.s.key}
                    x={x}
                    y={g.y}
                    width={bw}
                    height={g.h}
                    rx={i === segs.length - 1 ? 3 : 0}
                    className={`${g.s.className} bchart-seg`}
                  />
                ))}
                {k % every === 0 && (
                  <text x={x + bw / 2} y={H - 8} className="bchart-axis" textAnchor="middle">
                    {b.label}
                  </text>
                )}
              </g>
            );
          })}
          {goal && (
            <line
              x1={pad.l}
              x2={W - pad.r}
              y1={yOf(goal.value)}
              y2={yOf(goal.value)}
              className="ins-goal"
              aria-hidden="true"
            />
          )}
          <line x1={pad.l} x2={W - pad.r} y1={yOf(0)} y2={yOf(0)} className="bchart-base" />
        </svg>
        {h && (
          <div className="bchart-tip" role="status" style={{ left: `${((pad.l + (hi + 0.5) * slot) / W) * 100}%` }}>
            <strong>{h.label}</strong>
            {series.map((s) => (
              <span key={s.key} className="block small">
                {series.length > 1 && <i className={`wt ${s.className}`} aria-hidden="true" />} {s.label}:{" "}
                {fmt(s.value(h))}
              </span>
            ))}
          </div>
        )}
      </div>
    </figure>
  );
}

function Kpi({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: "warn" | "ok" }) {
  return (
    <div className={`dtile co-tile ins-kpi ${tone ? `tone-${tone}` : ""}`}>
      <span className="dtile-head">
        <span className="dtile-label">{label}</span>
      </span>
      <span className="dtile-value num">{value}</span>
      {sub && <span className="dtile-sub muted small">{sub}</span>}
    </div>
  );
}

/**
 * Bedrijfspagina › Inzichten (premium adresboek stap 2): opdrachten, kosten per maand (contract en los),
 * reactietijd tegen de afspraak, per gebouw, herhaalstoringen en de vergelijking met andere leveranciers.
 */
export function InsightsTab({ companyId }: { companyId: string }) {
  const [period, setPeriod] = useState<InsightPeriod>("12m");
  const [building, setBuilding] = useState("");
  const q = useQuery({
    queryKey: ["company-insights", companyId, period, building],
    queryFn: () =>
      api<CompanyInsightsDto>(
        `/companies/${companyId}/insights?periode=${period}${building ? `&gebouw=${building}` : ""}`,
      ),
    placeholderData: (prev) => prev,
  });
  // Gebouwen voor de keuze: uit de eerste (ongefilterde) lading, zodat de lijst niet krimpt na kiezen.
  const [buildings, setBuildings] = useState<CompanyInsightsDto["perBuilding"]>([]);
  useEffect(() => {
    if (q.data && !building && q.data.perBuilding.length >= buildings.length) setBuildings(q.data.perBuilding);
  }, [q.data, building, buildings.length]);

  const d = q.data;
  if (!d) return q.isError ? <p className="muted">{"Inzichten konden niet worden geladen."}</p> : <Loading />;
  const m = d.metrics;
  const amounts = m.contractCents !== null;
  const cost = (m.contractCents ?? 0) + (m.otherCents ?? 0);
  const periodText =
    period === "12m" ? "afgelopen 12 maanden" : period === "all" ? `sinds ${d.years.at(-1)}` : `in ${period}`;
  const where = building ? ` · ${buildings.find((b) => b.building.id === building)?.building.name ?? ""}` : "";
  const withResponse = d.buckets.some((b) => b.responseMinutes != null);
  const maxJobs = Math.max(1, ...d.perBuilding.map((p) => p.jobs));

  return (
    <div className="ins">
      <div className="ins-bar">
        <div className="chips" role="group" aria-label="Periode">
          {(["12m", ...d.years.slice(0, 3), "all"] as InsightPeriod[]).map((p) => (
            <button
              key={p}
              type="button"
              className="chip toggle"
              aria-pressed={period === p}
              onClick={() => setPeriod(p)}
            >
              {p === "12m" ? "12 maanden" : p === "all" ? "Alles" : p}
            </button>
          ))}
        </div>
        {buildings.length > 1 && (
          <select aria-label="Gebouw" value={building} onChange={(e) => setBuilding(e.target.value)}>
            <option value="">Alle gebouwen</option>
            {buildings.map((b) => (
              <option key={b.building.id} value={b.building.id}>
                {b.building.code} {b.building.name}
              </option>
            ))}
          </select>
        )}
      </div>

      <div className="dgrid co-tiles">
        <Kpi
          label="Opdrachten"
          value={String(m.jobs)}
          sub={
            [
              m.breakdowns && plural(m.breakdowns, "storing", "storingen"),
              m.tasks && plural(m.tasks, "taak", "taken"),
              m.visits && plural(m.visits, "uitvoering", "uitvoeringen"),
            ]
              .filter(Boolean)
              .join(" · ") || periodText
          }
        />
        {amounts ? (
          <Kpi label="Kosten" value={euro0(cost)} sub={m.jobs ? `${euro0(cost / m.jobs)} per opdracht` : periodText} />
        ) : (
          <Kpi
            label="Doorlooptijd"
            value={m.leadDays == null ? "–" : `${m.leadDays} d`}
            sub="mediaan, gemeld tot klaar"
          />
        )}
        <Kpi
          label="Reactietijd"
          value={fmtMinutes(m.responseMinutes)}
          sub={
            m.withinSlaPct != null
              ? `${m.withinSlaPct}% binnen afspraak van ${m.slaHours} uur`
              : m.responseMinutes != null
                ? `mediaan, gemeld tot begonnen${m.responseOfBreakdowns ? " (storingen)" : ""}`
                : "nog geen gestarte opdrachten"
          }
          tone={m.withinSlaPct == null ? undefined : m.withinSlaPct >= 90 ? "ok" : "warn"}
        />
        <Kpi
          label="Keuringen"
          value={String(m.inspections)}
          sub={m.passPct == null ? "geen keuringen" : `${m.passPct}% zonder afkeur`}
          tone={m.passPct == null ? undefined : m.passPct >= 90 ? "ok" : "warn"}
        />
      </div>

      {amounts && (
        <section className="panel co-panel" aria-label="Kosten per maand">
          <div className="co-head">
            <span className="grow">
              <h2>Kosten per {period === "all" ? "jaar" : "maand"}</h2>
              <span className="small muted co-block">
                {periodText}
                {where}
              </span>
            </span>
          </div>
          {cost > 0 ? (
            <>
              <Bars
                buckets={d.buckets}
                label={`Kosten per ${period === "all" ? "jaar" : "maand"}, contract en los`}
                fmt={formatEuro}
                axis={shortEuro}
                series={[
                  { key: "c", label: "Contract", className: "ins-contract", value: (b) => b.contractCents },
                  { key: "o", label: "Los en meerwerk", className: "ins-other", value: (b) => b.otherCents },
                ]}
              />
              <p className="small muted">
                {Math.round(((m.contractCents ?? 0) / cost) * 100)}% via contract ({euro0(m.contractCents ?? 0)}),{" "}
                {Math.round(((m.otherCents ?? 0) / cost) * 100)}% los ({euro0(m.otherCents ?? 0)}).
              </p>
            </>
          ) : (
            <p className="muted small">Nog geen kosten in deze periode.</p>
          )}
          <p className="hint">
            Contract: de bedragen van de posten, verdeeld over de maanden en gebouwen. Los: de kosten die bij een
            afgeronde taak zijn ingevuld.
          </p>
        </section>
      )}

      <div className="co-grid ins-grid">
        <section className="panel co-panel" aria-label="Reactietijd">
          <div className="co-head">
            <span className="grow">
              <h2>Reactietijd per {period === "all" ? "jaar" : "maand"}</h2>
              <span className="small muted co-block">
                Mediaan van gemeld tot begonnen{m.responseOfBreakdowns ? ", storingen" : ""}
              </span>
            </span>
          </div>
          {withResponse ? (
            <Bars
              buckets={d.buckets}
              label="Reactietijd per periode in uren"
              fmt={(v) => fmtMinutes(Math.round(v * 60))}
              axis={(v) => `${v.toLocaleString("nl-NL")} u`}
              goal={m.slaHours != null ? { value: m.slaHours, label: `Afspraak ${m.slaHours} uur` } : null}
              series={[
                {
                  key: "r",
                  label: "Reactietijd",
                  className: "ins-contract",
                  value: (b) => (b.responseMinutes ?? 0) / 60,
                },
              ]}
            />
          ) : (
            <p className="muted small">Nog geen opdrachten die zijn opgepakt in deze periode.</p>
          )}
          <dl className="kv">
            <dt>Op tijd klaar</dt>
            <dd className="num">{fmtPct(m.onTimePct)}</dd>
            <dt>Doorlooptijd</dt>
            <dd className="num">
              {m.leadDays == null ? "–" : `${m.leadDays.toLocaleString("nl-NL")} dagen (mediaan)`}
            </dd>
          </dl>
        </section>

        <section className="panel co-panel" aria-label="Per gebouw">
          <div className="co-head">
            <h2>Per gebouw</h2>
            <span className="small muted">opdrachten{amounts ? " · kosten" : ""}</span>
          </div>
          {d.perBuilding.length ? (
            <ul className="ins-blist">
              {d.perBuilding.map((p) => (
                <li key={p.building.id}>
                  <span className="ins-btop">
                    <Link to={buildingPath(p.building.id, "gebouw")}>
                      <span className="code">{p.building.code}</span> {p.building.name}
                    </Link>
                    <span className="num small">
                      <Meta items={[String(p.jobs), p.costCents != null && p.costCents > 0 && euro0(p.costCents)]} />
                    </span>
                  </span>
                  <span className="ins-track" aria-hidden="true">
                    <i style={{ width: `${(p.jobs / maxJobs) * 100}%` }} />
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="muted small">Geen opdrachten in deze periode.</p>
          )}
        </section>
      </div>

      <section className="panel co-panel" aria-label="Herhaalstoringen">
        <div className="co-head">
          <h2>Herhaalstoringen</h2>
          <span className="small muted">3 of meer aan één asset in 12 maanden</span>
        </div>
        {d.repeats.length ? (
          <ul className="co-list">
            {d.repeats.map((r) => (
              <li key={r.asset.id} className="co-row">
                <span className="co-ico" aria-hidden="true">
                  <IconRepeat size={18} />
                </span>
                <span className="grow">
                  <Link to={`${buildingPath(r.building.id, "assets")}?asset=${r.asset.id}`} className="co-rowtitle">
                    {r.asset.name}
                  </Link>
                  <Meta
                    className="small muted co-block"
                    items={[`${r.building.code} ${r.building.name}`, plural(r.count, "opdracht", "opdrachten")]}
                  />
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="muted small">Geen assets met herhaalde storingen.</p>
        )}
        <p className="hint">Mogelijk beter om te vervangen dan steeds te repareren.</p>
      </section>

      {d.compare && d.compare.length > 1 && (
        <section className="panel co-panel" aria-label="Vergelijken">
          <div className="co-head">
            <span className="grow">
              <h2>Vergelijken met je andere leveranciers</h2>
              <span className="small muted co-block">Afgelopen 12 maanden, alle gebouwen</span>
            </span>
          </div>
          <div className="tscroll">
            <table className="it ins-cmp" aria-label="Vergelijking leveranciers">
              <thead>
                <tr>
                  <th scope="col">Bedrijf</th>
                  <th scope="col" className="num">
                    Opdrachten
                  </th>
                  {amounts && (
                    <th scope="col" className="num">
                      Kosten
                    </th>
                  )}
                  <th scope="col" className="num">
                    Reactietijd
                  </th>
                  <th scope="col" className="num co-hide-narrow">
                    Op tijd
                  </th>
                  <th scope="col" className="num co-hide-narrow">
                    Herhaal&shy;storingen
                  </th>
                </tr>
              </thead>
              <tbody>
                {d.compare.map((x) => {
                  const me = x.company.id === companyId;
                  const late =
                    x.metrics.slaHours != null &&
                    x.metrics.responseMinutes != null &&
                    x.metrics.responseMinutes > x.metrics.slaHours * 60;
                  return (
                    <tr key={x.company.id} className={me ? "ins-me" : undefined} aria-current={me || undefined}>
                      <td className="tcell-title">
                        {me ? (
                          <strong>{x.company.name}</strong>
                        ) : (
                          <Link to={companyPath(x.company.id, "inzichten")}>{x.company.name}</Link>
                        )}
                        {x.company.trade && <div className="small muted">{x.company.trade}</div>}
                      </td>
                      <td className="num">{x.metrics.jobs}</td>
                      {amounts && (
                        <td className="num">
                          {(x.metrics.contractCents ?? 0) + (x.metrics.otherCents ?? 0)
                            ? euro0((x.metrics.contractCents ?? 0) + (x.metrics.otherCents ?? 0))
                            : "–"}
                        </td>
                      )}
                      <td className={`num${late ? " co-late" : ""}`}>
                        {fmtMinutes(x.metrics.responseMinutes)}
                        {x.metrics.slaHours != null && <span className="muted small"> / {x.metrics.slaHours} u</span>}
                      </td>
                      <td className="num co-hide-narrow">{fmtPct(x.metrics.onTimePct)}</td>
                      <td className="num co-hide-narrow">{x.metrics.repeats || "–"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="hint">
            Reactietijd in rood: langer dan de afspraak in het contract. Alleen voor beheerders van het adresboek, nooit
            voor de leverancier zelf.
          </p>
        </section>
      )}
    </div>
  );
}
